// Locale text-direction resolution for the i18n runtime (UPT-57 / UPT-39).
//
// The translation catalog (`dbo.i18n_locale.direction`) is the source of truth for
// per-locale direction. Until the `/api/v1/translations/languages` payload exposes
// `direction`, the front end resolves it from the well-known RTL locale set below so
// the runtime is already RTL-ready: adding an RTL locale renders correctly without a
// code change here, and a catalog-provided direction can be layered on via
// `registerLocaleDirections` once the API surfaces it.

export type TextDirection = 'ltr' | 'rtl';

/** Normalize a raw DOM `dir` attribute value to a known text direction (defaults to ltr). */
export const directionFromAttr = (value: string | null | undefined): TextDirection =>
  value === 'rtl' ? 'rtl' : 'ltr';

// Base-language codes that are written right-to-left. Compared against the language
// subtag only (e.g. `ar-EG` -> `ar`), so regional variants resolve correctly.
const RTL_LANGUAGES: ReadonlySet<string> = new Set([
  'ar', // Arabic
  'he', // Hebrew
  'fa', // Persian / Farsi
  'ur', // Urdu
  'ps', // Pashto
  'sd', // Sindhi
  'ug', // Uyghur
  'yi', // Yiddish
  'dv', // Divehi / Maldivian
]);

// Catalog-provided overrides, keyed by full locale code. Populated from the locale
// registry when available so the DB `direction` column wins over the static set.
const directionOverrides = new Map<string, TextDirection>();

const baseLanguage = (locale: string): string =>
  (locale || '').toLowerCase().split(/[-_]/)[0];

/**
 * Resolve the text direction for a locale code (e.g. `en`, `fr`, `ar-EG`).
 * Catalog overrides take precedence; otherwise the well-known RTL set decides.
 */
export const resolveDirection = (locale: string | null | undefined): TextDirection => {
  if (!locale) return 'ltr';
  const normalized = locale.toLowerCase();
  if (directionOverrides.has(normalized)) {
    return directionOverrides.get(normalized) as TextDirection;
  }
  return RTL_LANGUAGES.has(baseLanguage(normalized)) ? 'rtl' : 'ltr';
};

/**
 * Merge catalog-provided directions (from the locale registry) so DB values
 * override the static heuristic. Safe to call repeatedly as the registry loads.
 */
export const registerLocaleDirections = (
  entries: ReadonlyArray<{ locale: string; direction?: TextDirection | null }>,
): void => {
  for (const entry of entries) {
    if (!entry?.locale || !entry.direction) continue;
    directionOverrides.set(entry.locale.toLowerCase(), entry.direction);
  }
};

/**
 * Apply `lang`/`dir` to the document root for the active locale. No-op on the server.
 * Keeping this in one place means every locale switch updates direction consistently.
 */
export const applyDocumentDirection = (locale: string): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const direction = resolveDirection(locale);
  if (root.getAttribute('dir') !== direction) {
    root.setAttribute('dir', direction);
  }
  if (root.getAttribute('lang') !== locale) {
    root.setAttribute('lang', locale);
  }
};
